import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import ClassicLayout from './ClassicLayout'
import AuthenticatedLayout from './AuthenticatedLayout'
import MinimalLayout from './MinimalLayout'
import ClassicHomePage from '../pages/ClassicHomePage'
import DashboardPage from '../pages/DashboardPage'
import { ContactPage } from '../pages/ContactPage'
import ProfilePage from '../pages/ProfilePage'
import ChangePasswordPage from '../pages/ChangePasswordPage'
import AffiliatePage from '../pages/AffiliatePage'
import AffiliateWithdrawalPage from '../pages/affiliate/AffiliateWithdrawalPage'
import NumberSetPage from '../pages/NumberSetPage'
import NumberSetCreatePage from '../pages/NumberSetCreatePage'
import WinNumberPage from '../pages/WinNumberPage'
import TicketPage from '../pages/TicketPage'
import TicketDetailPage from '../pages/TicketDetailPage'
import LotteryResultPage from '../pages/LotteryResultPage'
import SetPage from '../pages/SetPage'
import SetTermsPage from '../pages/SetTermsPage'
import SetLottoGamePage from '../pages/SetLottoGamePage'
import YeekeePage from '../pages/YeekeePage'
import YeekeeTermsPage from '../pages/YeekeeTermsPage'
import YeekeeResultPage from '../pages/YeekeeResultPage'
import CommonTermsPage from '../pages/CommonTermsPage'
import LotteryBetPage from '../pages/LotteryBetPage'
import LottoPage from '../pages/LottoPage'
import TopupPage from '../pages/TopupPage'
import TopupConfirmPage from '../pages/TopupConfirmPage'
import TopupReservePage from '../pages/TopupReservePage'
import WithdrawalPage from '../pages/WithdrawalPage'
import TransactionPage from '../pages/TransactionPage'
import PointsMoneytopointPage from '../pages/PointsMoneytopointPage'
import PointsPointtomoneyPage from '../pages/PointsPointtomoneyPage'
import PointsRewardsPage from '../pages/PointsRewardsPage'
import PredictionPage from '../pages/PredictionPage'
import CashbackPage from '../pages/CashbackPage'
import PromotionPage from '../pages/PromotionPage'
import FinancialReportPage from '../pages/FinancialReportPage'
import FinancialReportBettingPage from '../pages/FinancialReportBettingPage'
import FinancialReportPendingPage from '../pages/FinancialReportPendingPage'
import CasinoPage from '../pages/CasinoPage'
import RegisterPage from '../pages/RegisterPage'
import RegisterOTPPage from '../pages/RegisterOTPPage'
import RegisterCompletePage from '../pages/RegisterCompletePage'
import LoginPage from '../pages/LoginPage'
import TestFirebaseSMSPage from '../pages/TestFirebaseSMSPage'
import type { TenantThemeConfig } from '../types/theme'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface AppRoutesProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const AppRoutes: React.FC<AppRoutesProps> = ({ themeConfig, assetConfig }) => {
  const { isAuthenticated, isLoading } = useAuth()

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-dark">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-white">กำลังตรวจสอบสถานะการเข้าสู่ระบบ...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Authenticated Routes */}
      {isAuthenticated ? (
        <>
          <Route 
            path="/dashboard" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <DashboardPage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/contact" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <ContactPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <ProfilePage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/change-password" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <ChangePasswordPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/affiliate" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <AffiliatePage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/affiliate-withdrawal" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <AffiliateWithdrawalPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/number-set" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <NumberSetPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/number-set/create" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <NumberSetCreatePage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/number-set/edit/:id" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <NumberSetCreatePage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/number-set/win-number" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <WinNumberPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/ticket" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TicketPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/ticket-detail/:id" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TicketDetailPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/lottery-result" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <LotteryResultPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/set" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <SetPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/set-terms" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <SetTermsPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/set-lotto-game" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <SetLottoGamePage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/yeekee" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <YeekeePage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/yeekee-terms" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <YeekeeTermsPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/yeekee-result" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <YeekeeResultPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/lottery-terms" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <CommonTermsPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/lottery-bet" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <LotteryBetPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/lotto" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <LottoPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/topup" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TopupPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/topup-confirm" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TopupConfirmPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/topup-reserve" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TopupReservePage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/withdrawal" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <WithdrawalPage />
              </AuthenticatedLayout>
            } 
          />
          <Route
            path="/cashback"
            element={
              <AuthenticatedLayout
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <CashbackPage />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/promotion"
            element={
              <AuthenticatedLayout
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <PromotionPage />
              </AuthenticatedLayout>
            }
          />
          <Route 
            path="/points" 
            element={<Navigate to="/points/moneytopoint" replace />} 
          />
          <Route 
            path="/points/moneytopoint" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <PointsMoneytopointPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/points/pointtomoney" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <PointsPointtomoneyPage />
              </AuthenticatedLayout>
            } 
          />
          <Route
            path="/points/rewards"
            element={
              <AuthenticatedLayout
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <PointsRewardsPage />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/points/games/prediction"
            element={
              <AuthenticatedLayout
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <PredictionPage />
              </AuthenticatedLayout>
            }
          />
          <Route path="/lottery/prediction" element={<Navigate to="/points/games/prediction" replace />} />
          <Route 
            path="/transaction" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TransactionPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/financial-report" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <FinancialReportPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/financial-report/betting-history" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <FinancialReportBettingPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/financial-report/pending" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <FinancialReportPendingPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/casino" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <CasinoPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/lobby-casino" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <CasinoPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/test-firebase-sms" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TestFirebaseSMSPage />
              </AuthenticatedLayout>
            } 
          />
          <Route 
            path="/register" 
            element={
              <MinimalLayout>
                <RegisterPage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </MinimalLayout>
            } 
          />
          <Route 
            path="/register/otp" 
            element={
              <MinimalLayout>
                <RegisterOTPPage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </MinimalLayout>
            } 
          />
          <Route 
            path="/register/complete" 
            element={
              <MinimalLayout>
                <RegisterCompletePage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </MinimalLayout>
            } 
          />
          <Route 
            path="/login" // Login for authenticated users (redirect to dashboard)
            element={<Navigate to="/dashboard" replace />}
          />
          {/* Redirect authenticated users from home to dashboard */}
          <Route 
            path="/" 
            element={
              <AuthenticatedLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <DashboardPage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </AuthenticatedLayout>
            } 
          />
          {/* Catch all unknown routes for authenticated users */}
          <Route 
            path="*" 
            element={<Navigate to="/dashboard" replace />} 
          />
        </>
      ) : (
        /* Unauthenticated Routes */
        <>
          <Route 
            path="/" 
            element={
              <ClassicLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <ClassicHomePage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </ClassicLayout>
            } 
          />
          <Route 
            path="/contact" 
            element={
              <ClassicLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <ContactPage />
              </ClassicLayout>
            } 
          />
          <Route 
            path="/test-firebase-sms" 
            element={
              <ClassicLayout 
                themeConfig={themeConfig}
                assetConfig={assetConfig}
              >
                <TestFirebaseSMSPage />
              </ClassicLayout>
            } 
          />
          <Route 
            path="/register" 
            element={
              <MinimalLayout>
                <RegisterPage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </MinimalLayout>
            } 
          />
          <Route 
            path="/register/otp" 
            element={
              <MinimalLayout>
                <RegisterOTPPage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </MinimalLayout>
            } 
          />
          <Route 
            path="/register/complete" 
            element={
              <MinimalLayout>
                <RegisterCompletePage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </MinimalLayout>
            } 
          />
          <Route 
            path="/login" 
            element={
              <MinimalLayout>
                <LoginPage 
                  themeConfig={themeConfig} 
                  assetConfig={assetConfig}
                />
              </MinimalLayout>
            } 
          />
          {/* Redirect unauthenticated users from dashboard to home */}
          <Route 
            path="/dashboard" 
            element={<Navigate to="/" replace />} 
          />
          {/* Catch all unknown routes for unauthenticated users */}
          <Route 
            path="*" 
            element={<Navigate to="/" replace />} 
          />
        </>
      )}
    </Routes>
  )
}

export default AppRoutes
